import { useCallback, useEffect, useRef } from 'react';
import type React from 'react';

/** Which way a magnified glyph rises: away from the screen edge its group sits on. */
export type DockRise = 'up' | 'down' | 'left' | 'right';

export interface DockMagnifyOptions {
  /** Scale of the glyph directly under the cursor. */
  magnify?: number;
  /** How many neighbours either side feel it. At 0 only the hovered glyph grows. */
  spread?: number;
  /** How far (px) the nearest glyph rises. */
  lift?: number;
  /**
   * Which way glyphs rise. Also sets the axis: a row (up/down) reads the
   * pointer's x, a column (left/right) reads its y.
   */
  rise?: DockRise;
}

/**
 * Raised cosine falloff: 1 under the cursor, easing to 0 at `spread + 1`
 * item pitches away, flat zero past it. `distance` is in item pitches and is
 * continuous, so the effect glides between items instead of stepping.
 */
export function dockFalloff(distance: number, spread: number): number {
  const reach = spread + 1;
  if (distance >= reach) return 0;
  return (1 + Math.cos((Math.PI * distance) / reach)) / 2;
}

/** The transform for one glyph at falloff `f`. */
export function dockTransform(f: number, { magnify, lift, rise }: Required<Omit<DockMagnifyOptions, 'spread'>>): string {
  if (f <= 0) return '';
  const d = (lift * f).toFixed(2);
  const move =
    rise === 'up' ? `translateY(-${d}px)`
      : rise === 'down' ? `translateY(${d}px)`
        : rise === 'left' ? `translateX(-${d}px)`
          : `translateX(${d}px)`;
  return `${move} scale(${(1 + (magnify - 1) * f).toFixed(3)})`;
}

/**
 * Distance-based dock magnification for any group of canvas controls.
 *
 * The group reads the pointer, the items do not: one handler on the container
 * measures every `[data-dock-item]` centre and writes a transform onto its
 * `[data-dock-glyph]` child. Only the glyph is transformed, so the buttons'
 * layout boxes never move and the measurements stay stable (no resize loop).
 * An item marked `data-dock-item="off"` is skipped and its glyph reset.
 *
 * The pitch is the smallest centre-to-centre gap between neighbours, so a
 * divider or a wide control in the row does not stretch the reach; a lone
 * button uses its own size.
 *
 * Transforms are written straight to the DOM inside a rAF rather than through
 * React state, so a sweep does not re-render the group on every move. The
 * pointer only counts inside the group's own box: a popover or results list
 * rendered inside the group's DOM does not drive it.
 *
 * Mouse only: touch and pen keep their normal tap and drag behaviour.
 * Reduced motion turns the effect off entirely.
 */
export function useDockMagnify<T extends HTMLElement>({
  magnify = 1.3,
  spread = 2,
  lift = 4,
  rise = 'up',
}: DockMagnifyOptions = {}) {
  const ref = useRef<T | null>(null);
  const frame = useRef<number | null>(null);
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const vertical = rise === 'left' || rise === 'right';

  const apply = useCallback(() => {
    frame.current = null;
    const root = ref.current;
    if (!root) return;
    const all = Array.from(root.querySelectorAll<HTMLElement>('[data-dock-item]'));
    const live = all.filter((el) => el.dataset.dockItem !== 'off');
    const glyphOf = (item: HTMLElement) => item.querySelector<HTMLElement>('[data-dock-glyph]');

    let at = pointer.current;
    if (at) {
      const box = root.getBoundingClientRect();
      if (at.x < box.left || at.x > box.right || at.y < box.top || at.y > box.bottom) at = null;
    }

    const centres = live.map((el) => {
      const r = el.getBoundingClientRect();
      return { c: vertical ? r.top + r.height / 2 : r.left + r.width / 2, size: vertical ? r.height : r.width };
    });
    let pitch = Infinity;
    for (let i = 1; i < centres.length; i++) {
      const gap = Math.abs(centres[i].c - centres[i - 1].c);
      if (gap > 0) pitch = Math.min(pitch, gap);
    }
    if (!Number.isFinite(pitch)) pitch = centres[0]?.size ?? 0;

    all.forEach((item) => {
      const glyph = glyphOf(item);
      if (glyph && !live.includes(item)) glyph.style.transform = '';
    });
    live.forEach((item, i) => {
      const glyph = glyphOf(item);
      if (!glyph) return;
      if (!at || pitch <= 0) {
        glyph.style.transform = '';
        return;
      }
      const d = Math.abs((vertical ? at.y : at.x) - centres[i].c) / pitch;
      glyph.style.transform = dockTransform(dockFalloff(d, spread), { magnify, lift, rise });
    });
  }, [magnify, spread, lift, rise, vertical]);

  const schedule = useCallback(() => {
    if (frame.current === null) frame.current = requestAnimationFrame(apply);
  }, [apply]);

  const reset = useCallback(() => {
    pointer.current = null;
    schedule();
  }, [schedule]);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      pointer.current = { x: e.clientX, y: e.clientY };
      schedule();
    },
    [schedule],
  );

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );

  return {
    ref,
    handlers: {
      onPointerMove,
      onPointerLeave: reset,
      // HTML5 drag-to-canvas suppresses pointer events; drop the zoom when it starts.
      onDragStartCapture: reset,
    },
  };
}
